import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');

  const openModal = (pkg = '') => {
    // Redirecionar para o quiz em vez de abrir modal, conforme fluxo solicitado anteriormente
    if (pkg) {
      navigate(`/quiz?package=${pkg}`);
    } else {
      navigate('/quiz');
    }
  };

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.feature-card, .step, .price-card, .testimonial').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-page-wrapper">
      <style>{`
        .home-page-wrapper {
          --primary: #8B5CF6;
          --primary-dark: #7C3AED;
          --secondary: #EC4899;
          --accent: #F59E0B;
          --dark: #1F2937;
          --light: #F9FAFB;
          --text: #111827;
          --text-light: #6B7280;
          --success: #10B981;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: var(--text);
          background: var(--light);
        }

        nav {
          background: white;
          padding: 1rem 0;
          position: sticky;
          top: 0;
          z-index: 1000;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo {
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .nav-links {
          display: flex;
          gap: 2rem;
          list-style: none;
          align-items: center;
        }

        .nav-links a {
          text-decoration: none;
          color: var(--text);
          font-weight: 500;
          transition: color 0.3s;
        }

        .nav-links a:hover {
          color: var(--primary);
        }

        .nav-cta {
          background: var(--primary);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          text-decoration: none;
          font-weight: 600;
          transition: background 0.3s;
          cursor: pointer;
          border: none;
        }

        .nav-cta:hover {
          background: var(--primary-dark);
        }

        .hero {
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          color: white;
          padding: 6rem 2rem;
          text-align: center;
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .hero h1 {
          font-size: 3.5rem;
          margin-bottom: 1rem;
          font-weight: 700;
          line-height: 1.2;
        }

        .hero p {
          font-size: 1.25rem;
          margin-bottom: 2rem;
          opacity: 0.95;
        }

        .hero-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn {
          padding: 1rem 2rem;
          border-radius: 0.5rem;
          text-decoration: none;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s;
          font-size: 1rem;
        }

        .btn-primary {
          background: var(--accent);
          color: white;
        }

        .btn-primary:hover {
          background: #DBBF00;
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(245, 158, 11, 0.3);
        }

        .btn-secondary {
          background: white;
          color: var(--primary);
          border: 2px solid white;
        }

        .btn-secondary:hover {
          background: transparent;
          color: white;
        }

        .features {
          max-width: 1200px;
          margin: 5rem auto;
          padding: 0 2rem;
        }

        .section-title {
          text-align: center;
          font-size: 2.5rem;
          margin-bottom: 1rem;
          color: var(--text);
        }

        .section-subtitle {
          text-align: center;
          font-size: 1.1rem;
          color: var(--text-light);
          margin-bottom: 3rem;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .feature-card {
          background: white;
          padding: 2rem;
          border-radius: 1rem;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
          transition: all 0.3s;
          text-align: center;
          opacity: 0;
        }

        .feature-card.fade-in-up {
          opacity: 1;
        }

        .feature-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 35px rgba(0,0,0,0.15);
        }

        .feature-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .feature-card h3 {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
          color: var(--primary);
        }

        .feature-card p {
          color: var(--text-light);
          font-size: 0.95rem;
        }

        .how-it-works {
          background: white;
          padding: 5rem 2rem;
          margin: 5rem 0;
        }

        .how-container {
          max-width: 1000px;
          margin: 0 auto;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        .step {
          position: relative;
          padding: 2rem;
          background: var(--light);
          border-radius: 1rem;
          border-left: 4px solid var(--primary);
          opacity: 0;
        }

        .step.fade-in-up {
          opacity: 1;
        }

        .step-number {
          position: absolute;
          top: -15px;
          left: 2rem;
          width: 40px;
          height: 40px;
          background: var(--primary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.2rem;
        }

        .step h3 {
          margin-top: 0.5rem;
          color: var(--text);
          margin-bottom: 0.5rem;
        }

        .step p {
          color: var(--text-light);
          font-size: 0.95rem;
        }

        .pricing {
          max-width: 900px;
          margin: 5rem auto;
          padding: 0 2rem;
        }

        .pricing-cards {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2rem;
          margin-top: 3rem;
        }

        .price-card {
          background: white;
          border-radius: 1rem;
          padding: 2rem;
          border: 2px solid #E5E7EB;
          transition: all 0.3s;
          position: relative;
          opacity: 0;
        }

        .price-card.fade-in-up {
          opacity: 1;
        }

        .price-card.featured {
          border-color: var(--primary);
          transform: scale(1.05);
          box-shadow: 0 20px 40px rgba(139, 92, 246, 0.2);
        }

        .price-badge {
          position: absolute;
          top: -15px;
          right: 1rem;
          background: var(--primary);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 2rem;
          font-size: 0.8rem;
          font-weight: 700;
        }

        .price-name {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .price-amount {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--primary);
          margin-bottom: 0.5rem;
        }

        .price-amount small {
          font-size: 1rem;
          color: var(--text-light);
          font-weight: 400;
        }

        .price-description {
          color: var(--text-light);
          margin-bottom: 1.5rem;
          font-size: 0.9rem;
        }

        .price-features {
          list-style: none;
          margin-bottom: 2rem;
        }

        .price-features li {
          padding: 0.5rem 0;
          border-bottom: 1px solid #F3F4F6;
          color: var(--text);
          font-size: 0.95rem;
        }

        .price-cta {
          width: 100%;
          padding: 1rem;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
        }

        .price-cta:hover {
          background: var(--primary-dark);
        }

        .testimonials {
          background: #F3F4F6;
          padding: 5rem 2rem;
        }

        .testimonials-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        .testimonial {
          background: white;
          padding: 2rem;
          border-radius: 1rem;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          opacity: 0;
        }

        .testimonial.fade-in-up {
          opacity: 1;
        }

        .stars {
          color: #F59E0B;
          margin-bottom: 1rem;
        }

        .testimonial-text {
          font-style: italic;
          margin-bottom: 1.5rem;
          color: var(--text);
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .author-avatar {
          width: 50px;
          height: 50px;
          background: var(--primary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
        }

        .author-info h4 {
          font-size: 1rem;
          margin-bottom: 0.1rem;
        }

        .author-info p {
          font-size: 0.85rem;
          color: var(--text-light);
        }

        .cta-section {
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          color: white;
          padding: 5rem 2rem;
          text-align: center;
        }

        .cta-section h2 {
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }

        .cta-section p {
          font-size: 1.2rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }

        footer {
          background: var(--dark);
          color: white;
          padding: 4rem 2rem 2rem;
        }

        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .footer-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 3rem;
          margin-bottom: 3rem;
        }

        .footer-section h4 {
          font-size: 1.2rem;
          margin-bottom: 1.5rem;
          color: white;
        }

        .footer-section ul {
          list-style: none;
        }

        .footer-section ul li {
          margin-bottom: 0.75rem;
        }

        .footer-section a {
          color: #D1D5DB;
          text-decoration: none;
          transition: color 0.3s;
        }

        .footer-section a:hover {
          color: var(--accent);
        }

        .footer-bottom {
          border-top: 1px solid #374151;
          padding-top: 2rem;
          text-align: center;
          color: #9CA3AF;
        }

        .social-links {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .social-links a {
          display: flex;
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.1);
          border-radius: 50%;
          align-items: center;
          justify-content: center;
          transition: background 0.3s;
          text-decoration: none;
        }

        .social-links a:hover {
          background: var(--accent);
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        @media (max-width: 768px) {
          .nav-links { display: none; }
          .hero h1 { font-size: 2rem; }
          .hero p { font-size: 1rem; }
          .section-title { font-size: 1.75rem; }
          .price-card.featured { transform: scale(1); }
          .cta-section h2 { font-size: 1.75rem; }
          .hero-buttons { flex-direction: column; }
          .btn { width: 100%; }
        }
      `}</style>

      <nav>
        <div className="nav-container">
          <div className="logo">🎵 Cantando Vidas</div>
          <ul className="nav-links">
            <li><a href="#features">Recursos</a></li>
            <li><a href="#how">Como Funciona</a></li>
            <li><a href="#pricing">Preços</a></li>
            <li><a href="#testimonials">Depoimentos</a></li>
            <li><button className="nav-cta" onClick={() => openModal()}>Criar Música</button></li>
          </ul>
        </div>
      </nav>

      <section className="hero">
        <div className="hero-content">
          <h1>Crie Sua Música Personalizada</h1>
          <p>Criamos músicas exclusivas para pessoas, famílias e eventos especiais, transformando momentos importantes em melodias inesquecíveis.</p>
          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={() => openModal()}>Criar Minha Música Agora</button>
            <button className="btn btn-secondary" onClick={() => document.getElementById('how').scrollIntoView({ behavior: 'smooth' })}>Ver Como Funciona</button>
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <h2 className="section-title">Por Que Escolher Cantando Vidas?</h2>
        <p className="section-subtitle">Serviço profissional, criativo e totalmente personalizado para você</p>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🎼</div>
            <h3>Canção Original por IA</h3>
            <p>Cada música é criada do zero por inteligência artificial, com base na sua história, sendo 100% única e personalizada.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">⏱️</div>
            <h3>Entrega Rápida</h3>
            <p>Músicas criadas e entregues em até 48h, prontas para emocionar no momento certo.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎯</div>
            <h3>Personalização Total</h3>
            <p>Escolha estilo, gênero, clima, duração e detalhes da letra. A IA adapta a música exatamente ao seu momento.</p>
          </div>
          
        </div>
      </section>

      <section id="how" className="how-it-works">
        <div className="how-container">
          <h2 className="section-title">Como Funciona</h2>
          <p className="section-subtitle">Simples, rápido e seguro. 3 passos para sua música personalizada</p>
          <div className="steps-grid">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Descreva Sua Ideia</h3>
              <p>Preencha nosso formulário contando seus desejos, emoções, histórias e preferências musicais.</p>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <h3>Criamos Sua Música</h3>
              <p>Com as informações preenchidas, transformamos sua história em uma música única, feita do seu jeito.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Você Recebe!</h3>
              <p>Entregamos um arquivo MP3 de alta qualidade pronto para ouvir, compartilhar e desfrutar.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="pricing">
        <h2 className="section-title">Nossos Pacotes</h2>
        <p className="section-subtitle">Escolha o tempo de entrega e quantidade de revisões que melhor se adequa a você</p>
        
        <div className="pricing-cards">
          {/* 24h com 1 revisão */}
          <div className="price-card">
            <div className="price-name">Turbo</div>
            
            {/* Preço antigo */}
            <div style={{
              fontSize: '1rem',
              color: '#5e6a7a',
              textDecoration: 'line-through',
              marginBottom: '0.25rem'
            }}>
              R$ 89,90
            </div>
            
            {/* Preço novo */}
            <div className="price-amount">R$ <strong>47,90</strong><small>/música</small></div>
            
            {/*<div className="price-description">Entrega em 24h</div>*/}
            <ul className="price-features">
              <li>1 música 100% personalizada</li>
              <li>1 revisão inclusa</li>
              <li>⚡ Entrega turbo em 24h</li>
            </ul>
            
            <button className="price-cta" onClick={() => openModal('24h', {time: '24h'})}>
              Escolher Pacote
              
            </button>
            
          </div>

          

          {/* 48h com 1 revisão */}
          <div className="price-card">
            <div className="price-name">Essencial</div>
            <div className="price-badge">⭐ Mais Popular</div>
            {/* Preço antigo */}
            <div style={{
              fontSize: '1rem',
              color: '#5e6a7a',
              textDecoration: 'line-through',
              marginBottom: '0.25rem'
            }}>
              R$ 59,90
            </div>
            
            {/* Preço novo */}
            <div className="price-amount">R$ <strong>27,90</strong><small>/música</small></div>
            
            {/*<div className="price-description">Melhor custo-benefício</div>*/}
            <ul className="price-features">
              <li>1 música 100% personalizada</li>
              <li>1 revisão inclusa</li>
              <li>⏱️ Entrega em 48h</li>
            </ul>
            <button className="price-cta" onClick={() => openModal('48h', {time: '48h', revs: 1, price: 27.90})}>
              Escolher Pacote
            </button>
          </div>

          
        </div>

        <p className="pricing-note" style={{textAlign: 'center', fontSize: '0.9rem', color: 'var(--text-light)', marginTop: '2rem'}}>
          Todos incluem garantia de satisfação. Pagamento seguro via Mercado Pago.
        </p>
      </section>



      <section id="testimonials" className="testimonials">
        <div className="testimonials-container">
          <h2 className="section-title">O Que Nossos Clientes Dizem</h2>
          <p className="section-subtitle">Histórias reais de pessoas que transformaram seus momentos em música</p>
          <div className="testimonials-grid">
            <div className="testimonial">
              <div className="stars">★★★★★</div>
              <p className="testimonial-text">"Fiz uma música para pedir casamento e ficou perfeita! Minha noiva chorou de emoção. Recomendo demais!"</p>
              <div className="testimonial-author">
                <div className="author-avatar">CR</div>
                <div className="author-info">
                  <h4>Carlos R.</h4>
                  <p>São Paulo, SP</p>
                </div>
              </div>
            </div>
            <div className="testimonial">
              <div className="stars">★★★★★</div>
              <p className="testimonial-text">"Criei uma música para meu pai com memórias da nossa vida juntos. Ele adorou! Que presente inesquecível."</p>
              <div className="testimonial-author">
                <div className="author-avatar">AM</div>
                <div className="author-info">
                  <h4>Ana M.</h4>
                  <p>Rio de Janeiro, RJ</p>
                </div>
              </div>
            </div>
            <div className="testimonial">
              <div className="stars">★★★★★</div>
              <p className="testimonial-text">"Qualidade profissional, equipe atenciosa e entrega no prazo. Voltaria a fazer com certeza!"</p>
              <div className="testimonial-author">
                <div className="author-avatar">JP</div>
                <div className="author-info">
                  <h4>João P.</h4>
                  <p>Belo Horizonte, MG</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>Pronto para Criar Sua Música?</h2>
        <p>Não deixe seus sentimentos em silêncio. Transforme-os em uma melodia única e inesquecível.</p>
        <button className="btn btn-secondary" onClick={() => openModal()}>Começar Agora</button>
      </section>

      <footer>
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-section">
              <h4>Cantando Vidas</h4>
              <p style={{ color: '#D1D5DB' }}>Transformando emoções em melodias desde 2024.</p>
              <div className="social-links">
                <a href="https://www.instagram.com/cantandovidas_/" title="Instagram">📱</a>
                <a href="https://wa.me/5521998634554?text=Olá, vim do site da Cantando Vidas." title="WhatsApp">💬</a>
              </div>
            </div>
            <div className="footer-section">
              <h4>Serviços</h4>
              <ul>
                <li><a href="#pricing">Pacotes</a></li>
                <li><a href="#how">Como Funciona</a></li>
                <li><a href="#features">Recursos</a></li>
                <li><button style={{ background: 'none', border: 'none', color: '#D1D5DB', cursor: 'pointer', padding: 0, font: 'inherit' }} onClick={() => openModal()}>Começar</button></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Informações</h4>
              <ul>
                <li><a href="#">Sobre Nós</a></li>
                <li><a href="#">FAQ</a></li>
                <li><a href="#">Contato</a></li>
                <li><a href="#">Política de Privacidade</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Contato</h4>
              <ul>
                <li><a href="mailto:contato@Cantando Vidas.com">cantandovidas@gmail.com</a></li>
                <li><a href="tel:+5521998634554">(21) 99863-4554</a></li>
                <li>Rio de Janeiro, RJ</li>
                <li>Disponível 24/7</li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Cantando Vidas. Todos os direitos reservados. | Criando músicas que tocam o coração.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
